import React, { useState, useEffect } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
  XMarkIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const CookieConsent = () => {
  const { config, consentGiven, grantConsent, revokeConsent } = useAnalytics();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: true,
    marketing: true,
    functional: true
  });

  useEffect(() => {
    // Show banner if consent is required and not given
    if (config?.privacy?.cookie_consent_required && !consentGiven) {
      setShowBanner(true);
    }
  }, [config, consentGiven]);

  const handleAcceptAll = () => {
    grantConsent();
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    setPreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    });
    revokeConsent();
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    if (preferences.analytics || preferences.marketing || preferences.functional) {
      grantConsent();
    } else {
      revokeConsent();
    }
    setShowBanner(false);
    setShowDetails(false);
  };

  const handlePreferenceChange = (type) => {
    if (type === 'necessary') return; // Can't change necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" dir="rtl">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        {!showDetails ? (
          // Simple Banner
          <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-4">
                <ShieldCheckIcon className="h-8 w-8 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900">
                    אנחנו משתמשים בעוגיות
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    אנחנו משתמשים בעוגיות כדי לשפר את החוויה שלך, לנתח תנועה ולהציג תוכן מותאם אישית. 
                    על ידי המשך הגלישה אתה מסכים לשימוש בעוגיות.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowDetails(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Cog6ToothIcon className="w-4 h-4 ml-2" />
                  הגדרות
                </button>
                
                <button
                  onClick={handleRejectAll}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  דחה הכל
                </button>
                
                <button
                  onClick={handleAcceptAll}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <CheckIcon className="w-4 h-4 ml-2" />
                  קבל הכל
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Detailed Preferences
          <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  הגדרות פרטיות ועוגיות
                </h2>
                <button
                  onClick={() => setShowDetails(false)}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="py-6 space-y-6">
                <p className="text-gray-600">
                  אנחנו משתמשים בעוגיות ובטכנולוגיות דומות כדי לספק לך חוויית גלישה מותאמת אישית, 
                  לנתח את השימוש באתר ולשפר את השירותים שלנו. תוכל לבחור אילו סוגי עוגיות לאפשר:
                </p>

                {/* Cookie Categories */}
                <div className="space-y-4">
                  {/* Necessary Cookies */}
                  <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h3 className="text-lg font-medium text-gray-900">עוגיות חיוניות</h3>
                        <span className="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          חובה
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">
                        עוגיות אלו חיוניות לפעולת האתר ואינן ניתנות לביטול. הן כוללות עוגיות אבטחה, 
                        התחברות ושמירת העדפות בסיסיות.
                      </p>
                    </div>
                    <div className="mr-4">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled={true}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded opacity-50"
                      />
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">עוגיות אנליטיקה</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        עוגיות אלו עוזרות לנו להבין כיצד המבקרים משתמשים באתר, איזה דפים הכי פופולריים 
                        וכיצד לשפר את החוויה. הנתונים נאספים באופן אנונימי.
                      </p>
                      <div className="mt-2 text-xs text-gray-500">
                        כולל: Google Analytics, Microsoft Clarity
                      </div>
                    </div>
                    <div className="mr-4">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange('analytics')}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">עוגיות שיווק</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        עוגיות אלו משמשות להצגת פרסומות רלוונטיות ולמדידת יעילות הקמפיינים השיווקיים שלנו. 
                        הן עוזרות לנו להציג לך תוכן מותאם לתחומי העניין שלך.
                      </p>
                      <div className="mt-2 text-xs text-gray-500">
                        כולל: Facebook Pixel, Google Ads
                      </div>
                    </div>
                    <div className="mr-4">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => handlePreferenceChange('marketing')}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  </div>

                  {/* Functional Cookies */}
                  <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">עוגיות פונקציונליות</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        עוגיות אלו מאפשרות פונקציות משופרות כמו צ'אט תמיכה, שמירת העדפות אישיות 
                        ותכונות אינטראקטיביות אחרות שמשפרות את החוויה שלך.
                      </p>
                      <div className="mt-2 text-xs text-gray-500">
                        כולל: צ'אט תמיכה, העדפות שפה, נושא צבעים
                      </div>
                    </div>
                    <div className="mr-4">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={() => handlePreferenceChange('functional')}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  </div>
                </div>

                {/* Privacy Links */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-600">
                    למידע נוסף על איך אנחנו משתמשים בעוגיות ומגנים על הפרטיות שלך, קרא את{' '}
                    <a href="/privacy-policy" className="text-blue-600 hover:text-blue-500 underline">
                      מדיניות הפרטיות
                    </a>{' '}
                    ואת{' '}
                    <a href="/cookie-policy" className="text-blue-600 hover:text-blue-500 underline">
                      מדיניות העוגיות
                    </a>{' '}
                    שלנו.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleRejectAll}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  דחה הכל
                </button>
                
                <button
                  onClick={handleSavePreferences}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  שמור העדפות
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
